import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Card,
  Table,
  Spin,
  Alert,
  Modal,
  Button,
  message,
  Tooltip,
  Tag,
  Input,
  Select,
  Pagination,
  Tabs,
  Space,
  Typography,
} from "antd";
import { $bucketUrlHost, bucketUrl } from "http/index";
import {
  useFileShares,
  FileShare,
  useStorageGateways,
  useStorageGatewaysByIds,
  StorageGateway,
  GATEWAY_PAGE_SIZE,
} from "./hooks/useFileShares";
import {
  PlusOutlined,
  DeleteOutlined,
  CopyOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { useMsal } from "@azure/msal-react";
import { getHeaders } from "helpers";
import { getAccessToken } from "config/token";
import "./style.scss";

const CodeBlock = ({ children }: { children: React.ReactNode }) => {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = () => {
    const text =
      typeof children === "string"
        ? children
        : (children as React.ReactElement)?.props?.children ?? "";
    navigator.clipboard.writeText(String(text).trim()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div className="code-block">
      <pre className="mount-instructions__pre">{children}</pre>
      <button
        className={`code-block__copy${
          copied ? " code-block__copy--copied" : ""
        }`}
        onClick={handleCopy}
        title={copied ? "Copied!" : "Copy"}
        type="button"
      >
        {copied ? <CheckOutlined /> : <CopyOutlined />}
      </button>
    </div>
  );
};

/**
 * Extract a human-readable message from an API error.
 * Handles:
 *  - FastAPI / 409 HTTP exceptions: { detail: string }
 *  - Pydantic validation errors:    { detail: [{ loc, msg, type }] }
 *  - Generic JS Error objects
 */
const extractApiError = (err: unknown, fallback: string): string => {
  if (err && typeof err === "object" && "response" in err) {
    const data = (err as { response?: { data?: unknown } }).response?.data;
    if (data && typeof data === "object") {
      const detail = (data as { detail?: unknown }).detail;
      if (typeof detail === "string" && detail) return detail;
      if (Array.isArray(detail) && detail.length > 0) {
        return detail
          .map((e: { loc?: string[]; msg?: string }) =>
            e.loc?.length ? `${e.loc.join(".")}: ${e.msg ?? ""}` : e.msg ?? "",
          )
          .filter(Boolean)
          .join("; ");
      }
    }
  }
  if (err && typeof err === "object" && "message" in err) {
    return (err as { message?: string }).message || fallback;
  }
  return fallback;
};

const SHARE_NAME_RE = /^[\w_-]+$/;
const PREFIX_RE = /^[\w/_-]+\/$/;
const MAX_SHARE_NAME = 256;
const MAX_PREFIX = 1024;
const MAX_DESCRIPTION = 1024;

const statusColor = (status: string): string => {
  const s = String(status ?? "").toUpperCase();
  if (s === "RUNNING" || s === "AVAILABLE") return "green";
  if (s === "STOPPED" || s === "OFFLINE") return "red";
  return "orange";
};

const GatewayTooltipContent = ({ gw }: { gw: StorageGateway }) => (
  <div>
    <div>
      <strong>Description:</strong> {gw.description}
    </div>
    <div>
      <strong>Auth type:</strong> {gw.auth_type}
    </div>
    <div>
      <strong>Region:</strong> {gw.region}
    </div>
    {gw.allowed_ad_groups?.length > 0 && (
      <div>
        <strong>Allowed Entra ID groups:</strong>
        <ul style={{ margin: "4px 0 0 0", paddingLeft: 16 }}>
          {gw.allowed_ad_groups.map(g => (
            <li key={g.id}>
              {g.name} ({g.id})
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

const MountInstructionsModal = ({
  share,
  ipAddress,
  visible,
  onClose,
}: {
  share: FileShare | null;
  ipAddress: string;
  visible: boolean;
  onClose: () => void;
}) => {
  if (!share) return null;
  const sharePath = share.prefix || share.name;
  const isSMB = share.share_type === "SMB";
  const isNFS = share.share_type === "NFS";

  const smbLinuxTab = {
    key: "linux",
    label: "Linux",
    children: (
      <div className="mount-instructions">
        <p>
          Mount the SMB share on Linux using the CIFS kernel module. Make sure
          the <code>cifs-utils</code> package is installed first.
        </p>
        <p className="mount-instructions__label">Install dependencies</p>
        <CodeBlock>sudo apt-get install -y cifs-utils</CodeBlock>
        <p className="mount-instructions__label">Create mount point</p>
        <CodeBlock>{`sudo mkdir -p /mnt/${share.name}`}</CodeBlock>
        <p className="mount-instructions__label">Mount the share</p>
        <CodeBlock>{`sudo mount -t cifs //${ipAddress}/${sharePath} /mnt/${share.name} \\\n  -o username=<username>,password=<password>,vers=3.0`}</CodeBlock>
        <p className="mount-instructions__label">Persist across reboots</p>
        <p>
          Add the following line to <code>/etc/fstab</code>:
        </p>
        <CodeBlock>{`//${ipAddress}/${sharePath}  /mnt/${share.name}  cifs  username=<username>,password=<password>,vers=3.0  0  0`}</CodeBlock>
      </div>
    ),
  };

  const nfsLinuxTab = {
    key: "linux",
    label: "Linux",
    children: (
      <div className="mount-instructions">
        <p>
          Mount the NFS share on Linux using the native NFS client. Make sure
          the <code>nfs-common</code> package is installed.
        </p>
        <p className="mount-instructions__label">Install dependencies</p>
        <CodeBlock>sudo apt-get install -y nfs-common</CodeBlock>
        <p className="mount-instructions__label">Create mount point</p>
        <CodeBlock>{`sudo mkdir -p /mnt/${share.name}`}</CodeBlock>
        <p className="mount-instructions__label">Mount the share</p>
        <CodeBlock>{`sudo mount -t nfs ${ipAddress}:/${sharePath} /mnt/${share.name}`}</CodeBlock>
        <p className="mount-instructions__label">Persist across reboots</p>
        <p>
          Add the following line to <code>/etc/fstab</code>:
        </p>
        <CodeBlock>{`${ipAddress}:/${sharePath}  /mnt/${share.name}  nfs  defaults  0  0`}</CodeBlock>
      </div>
    ),
  };

  const smbWindowsTab = {
    key: "windows",
    label: "Windows",
    children: (
      <div className="mount-instructions">
        <p>
          Map the SMB share as a network drive. You can use File Explorer, the
          command line, or PowerShell.
        </p>
        <p className="mount-instructions__label">File Explorer</p>
        <p>
          Open <em>This PC</em> → <em>Map network drive</em>, choose a drive
          letter, and enter the following path:
        </p>
        <CodeBlock>{`\\\\${ipAddress}\\${sharePath}`}</CodeBlock>
        <p className="mount-instructions__label">Command Prompt (net use)</p>
        <CodeBlock>{`net use Z: \\\\${ipAddress}\\${sharePath} /user:<username> <password> /persistent:yes`}</CodeBlock>
        <p className="mount-instructions__label">PowerShell (persistent)</p>
        <CodeBlock>{`$cred = Get-Credential\nNew-PSDrive -Name "Z" -PSProvider FileSystem -Root "\\\\${ipAddress}\\${sharePath}" -Credential $cred -Persist`}</CodeBlock>
      </div>
    ),
  };

  const tabs = [
    ...(isSMB || isNFS ? [isSMB ? smbLinuxTab : nfsLinuxTab] : []),
    ...(isSMB ? [smbWindowsTab] : []),
  ];

  return (
    <Modal
      title={`Mount Share: ${share.name} (${share.share_type})`}
      open={visible}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
      width={640}
    >
      <Tabs items={tabs} />
    </Modal>
  );
};

const buildColumns = (gatewayMap: Record<string, StorageGateway>) => [
  {
    title: "Gateway",
    dataIndex: "gateway_id",
    key: "gateway_id",
    render: (gatewayId: string) => {
      const gw = gatewayMap[gatewayId];
      const cell = (
        <div>
          <div className="gateway-cell__id">{gatewayId}</div>
          {gw && (
            <>
              <div className="gateway-cell__name">{gw.name}</div>
              <Tag color={statusColor(gw.state)} className="gateway-cell__tag">
                {gw.state}
              </Tag>
            </>
          )}
        </div>
      );
      if (!gw) return cell;
      return (
        <Tooltip title={<GatewayTooltipContent gw={gw} />} placement="right">
          {cell}
        </Tooltip>
      );
    },
  },
  // Share ID is hidden but available in dataSource for future use
  {
    title: "IP Address",
    dataIndex: "gateway_id",
    key: "ip_address",
    render: (gatewayId: string) => gatewayMap[gatewayId]?.ip_address ?? "",
  },
  {
    title: "Name",
    dataIndex: "name",
    key: "name",
    render: (name: string, record: FileShare) => (
      <div>
        <div>{name}</div>
        {record.description && (
          <div className="share-cell__description">{record.description}</div>
        )}
      </div>
    ),
  },
  {
    title: "Prefix",
    dataIndex: "prefix",
    key: "prefix",
    render: (prefix: string) =>
      prefix ? (
        <code className="share-cell__prefix">{prefix}</code>
      ) : (
        <span className="share-cell__empty">—</span>
      ),
  },
  {
    title: "Share Type",
    dataIndex: "share_type",
    key: "share_type",
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (status: string) => <Tag color={statusColor(status)}>{status}</Tag>,
  },
  {
    title: "Actions",
    key: "actions",
    render: (
      _: unknown,
      record: FileShare & {
        onDelete: (row: FileShare) => void;
        onMount: (row: FileShare) => void;
      },
    ) => (
      <Space>
        <Button onClick={() => record.onMount(record)}>Mount</Button>
        <Button
          danger
          icon={<DeleteOutlined />}
          onClick={() => record.onDelete(record)}
        />
      </Space>
    ),
  },
];

const FileShares = () => {
  const { id: pondId } = useParams();
  const { shares, loading, error, fetchShares } = useFileShares(pondId || "");
  const msalInstance = useMsal();

  const [gatewayPage, setGatewayPage] = useState(1);
  const {
    gateways,
    total: gatewayTotal,
    loading: gatewaysLoading,
  } = useStorageGateways(gatewayPage);

  // Fetch only the gateways that are referenced by existing shares
  const shareGatewayIds = useMemo(
    () => [
      ...new Set(shares.map((s: FileShare) => s.gateway_id).filter(Boolean)),
    ],
    [shares],
  );
  const { gateways: tableGateways } = useStorageGatewaysByIds(shareGatewayIds);

  const gatewayMap = useMemo(
    () =>
      Object.fromEntries<StorageGateway>(tableGateways.map(gw => [gw.id, gw])),
    [tableGateways],
  );

  const columns = useMemo(() => buildColumns(gatewayMap), [gatewayMap]);

  const [deletingShare, setDeletingShare] = useState<FileShare | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState<string>("");
  const [selectedGatewayData, setSelectedGatewayData] =
    useState<StorageGateway | null>(null);
  const [sharePrefix, setSharePrefix] = useState("");
  const [shareDescription, setShareDescription] = useState("");
  const [shareName, setShareName] = useState("");
  const [shareType, setShareType] = useState<"SMB">("SMB");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [mountingShare, setMountingShare] = useState<FileShare | null>(null);
  const [mountModalVisible, setMountModalVisible] = useState(false);

  const openCreateModal = () => {
    setCreateModalVisible(true);
    setGatewayPage(1);
  };

  const closeCreateModal = () => {
    setCreateModalVisible(false);
    setSelectedGateway("");
    setSelectedGatewayData(null);
    setSharePrefix("");
    setShareDescription("");
    setShareName("");
    setShareType("SMB");
    setFieldErrors({});
  };

  // Attach onDelete and onMount handlers to each row
  const dataSource = Array.isArray(shares)
    ? shares.map((share: FileShare) => ({
        ...share,
        onDelete: (row: FileShare) => {
          setDeletingShare(row);
          setModalVisible(true);
        },
        onMount: (row: FileShare) => {
          setMountingShare(row);
          setMountModalVisible(true);
        },
      }))
    : [];

  const handleDelete = async () => {
    if (!deletingShare || !pondId) return;
    setDeleteLoading(true);
    try {
      const token = await getAccessToken(msalInstance);
      await $bucketUrlHost.delete(
        `${bucketUrl}v2/s3buckets/${pondId}/storage-gateways/shares/${deletingShare.id}`,
        {
          headers: getHeaders(token?.token),
        },
      );
      message.success("Share deleted successfully");
      setModalVisible(false);
      setDeletingShare(null);
      fetchShares();
    } catch (err: unknown) {
      message.error(extractApiError(err, "Failed to delete share"));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCreateShare = async () => {
    const errors: Record<string, string> = {};
    if (!selectedGateway) errors.gateway = "Please select a storage gateway.";
    if (!shareName) {
      errors.shareName = "Share name is required.";
    } else if (shareName.length > MAX_SHARE_NAME) {
      errors.shareName = `Share name must be at most ${MAX_SHARE_NAME} characters.`;
    } else if (!SHARE_NAME_RE.test(shareName)) {
      errors.shareName =
        "Only letters, digits, underscores and hyphens are allowed.";
    }
    if (sharePrefix) {
      if (sharePrefix.length > MAX_PREFIX) {
        errors.sharePrefix = `Prefix must be at most ${MAX_PREFIX} characters.`;
      } else if (!PREFIX_RE.test(sharePrefix)) {
        errors.sharePrefix =
          "Prefix must match the pattern ^[\\w/_-]+/$ (word chars, slashes, hyphens, must end with /).";
      }
    }
    if (shareDescription && shareDescription.length > MAX_DESCRIPTION) {
      errors.shareDescription = `Description must be at most ${MAX_DESCRIPTION} characters.`;
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    if (!pondId) return;
    setCreateLoading(true);
    try {
      const token = await getAccessToken(msalInstance);
      await $bucketUrlHost.post(
        `v2/s3buckets/${pondId}/storage-gateways/shares/`,
        {
          gateway_id: selectedGateway,
          prefix: sharePrefix,
          description: shareDescription,
          share_name: shareName,
          share_type: shareType,
        },
        {
          headers: getHeaders(token?.token),
        },
      );
      message.success("Share created successfully");
      closeCreateModal();
      fetchShares();
    } catch (err: unknown) {
      message.error(extractApiError(err, "Failed to create share"));
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <div className="file-shares-tab">
      <Card
        title="File Shares"
        className="pond-page-card"
        extra={
          <Button
            type="primary"
            size="middle"
            className="btn"
            icon={<PlusOutlined />}
            onClick={openCreateModal}
          >
            Create Share
          </Button>
        }
      >
        <div className="static-card-descr">
          Storage Gateways expose your data pond as network file shares using
          the SMB or NFS protocol. Each share is backed by a gateway appliance
          running in your network and maps to a path inside the S3 bucket. You
          can mount the share on any compatible client without needing direct
          AWS credentials. More details are available{" "}
          <a
            href="https://docs.int.bayer.com/cloud/smart-cloud-automation/atscale/data-at-scale/datapond/user_guide/#file-shares"
            target="_blank"
            rel="noopener noreferrer"
          >
            here
          </a>
          .
        </div>
        {error && <Alert type="error" message={error} showIcon />}
        {loading ? (
          <Spin />
        ) : (
          <Table
            columns={columns}
            dataSource={dataSource}
            rowKey="id"
            pagination={false}
            className="file-shares-table"
          />
        )}
        <Modal
          title="Delete Share"
          open={modalVisible}
          onOk={handleDelete}
          onCancel={() => setModalVisible(false)}
          confirmLoading={deleteLoading}
          okText="Delete"
          okButtonProps={{ danger: true }}
        >
          Are you sure you want to delete this share?
        </Modal>
        <MountInstructionsModal
          share={mountingShare}
          ipAddress={
            mountingShare
              ? gatewayMap[mountingShare.gateway_id]?.ip_address ?? ""
              : ""
          }
          visible={mountModalVisible}
          onClose={() => {
            setMountModalVisible(false);
            setMountingShare(null);
          }}
        />
        <Modal
          title="Create Share"
          open={createModalVisible}
          onOk={handleCreateShare}
          onCancel={closeCreateModal}
          confirmLoading={createLoading}
          okText="Create Share"
          cancelText="Cancel"
        >
          <div style={{ marginBottom: 16 }}>
            <label>
              Storage Gateway <Typography.Text type="danger">*</Typography.Text>
            </label>
            <Select
              style={{ width: "100%" }}
              loading={gatewaysLoading}
              value={selectedGateway || undefined}
              onChange={value => {
                setSelectedGateway(value);
                setSelectedGatewayData(
                  gateways.find(g => g.id === value) ?? null,
                );
                setFieldErrors(prev => ({ ...prev, gateway: "" }));
              }}
              placeholder="Select Storage Gateway"
              optionLabelProp="label"
              status={fieldErrors.gateway ? "error" : ""}
            >
              {gateways.map((gateway: StorageGateway) => (
                <Select.Option
                  key={gateway.id}
                  value={gateway.id}
                  label={gateway.name}
                >
                  <div className="gateway-option__header">
                    <strong>{gateway.name}</strong>
                    <Tag
                      color={statusColor(gateway.state)}
                      className="gateway-option__tag"
                    >
                      {gateway.state}
                    </Tag>
                  </div>
                  <div
                    className="gateway-option__meta"
                    title={`${gateway.description} · ${gateway.auth_type} · ${gateway.region}`}
                  >
                    {gateway.description} · {gateway.auth_type} ·{" "}
                    {gateway.region}
                  </div>
                </Select.Option>
              ))}
            </Select>
            {fieldErrors.gateway && (
              <Typography.Text type="danger" style={{ fontSize: 12 }}>
                {fieldErrors.gateway}
              </Typography.Text>
            )}
            {gatewayTotal > GATEWAY_PAGE_SIZE && (
              <Pagination
                className="gateway-select__pagination"
                current={gatewayPage}
                total={gatewayTotal}
                pageSize={GATEWAY_PAGE_SIZE}
                onChange={setGatewayPage}
                size="small"
                simple
              />
            )}
            {selectedGateway &&
              selectedGatewayData &&
              (() => {
                const gw = selectedGatewayData;
                return (
                  <div className="gateway-info-panel">
                    <div className="gateway-info-panel__row">
                      <span className="gateway-info-panel__label">
                        Description
                      </span>
                      <span>{gw.description}</span>
                    </div>
                    <div className="gateway-info-panel__row">
                      <span className="gateway-info-panel__label">
                        Auth type
                      </span>
                      <span>{gw.auth_type}</span>
                    </div>
                    <div className="gateway-info-panel__row">
                      <span className="gateway-info-panel__label">Region</span>
                      <span>{gw.region}</span>
                    </div>
                    <div className="gateway-info-panel__row">
                      <span className="gateway-info-panel__label">
                        IP Address
                      </span>
                      <span>{gw.ip_address}</span>
                    </div>
                    <div className="gateway-info-panel__row">
                      <span className="gateway-info-panel__label">Status</span>
                      <Tag color={statusColor(gw.state)}>{gw.state}</Tag>
                    </div>
                    {gw.allowed_ad_groups?.length > 0 && (
                      <div className="gateway-info-panel__groups">
                        <div className="gateway-info-panel__groups-header">
                          <span>Required Entra ID Groups</span>
                          <Tooltip
                            title="Access to this storage gateway is restricted. To create a file share, you must be a member of at least one of the Entra ID groups listed below. Contact your administrator if you need access."
                            placement="top"
                          >
                            <span className="gateway-info-panel__help-icon">
                              ?
                            </span>
                          </Tooltip>
                        </div>
                        <ul className="gateway-info-panel__groups-list">
                          {gw.allowed_ad_groups.map(g => (
                            <li key={g.id}>
                              <span className="gateway-info-panel__group-name">
                                {g.name}
                              </span>
                              <span className="gateway-info-panel__group-id">
                                {g.id}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })()}
          </div>
          <div style={{ marginBottom: 16 }}>
            <label>
              Share Type <Typography.Text type="danger">*</Typography.Text>
            </label>
            <Select
              style={{ width: "100%" }}
              value={shareType}
              onChange={setShareType}
            >
              <Select.Option value="SMB">SMB</Select.Option>
            </Select>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label>
              Share Name <Typography.Text type="danger">*</Typography.Text>
            </label>
            <Input
              value={shareName}
              onChange={e => {
                setShareName(e.target.value);
                setFieldErrors(prev => ({ ...prev, shareName: "" }));
              }}
              placeholder="Enter share name"
              maxLength={MAX_SHARE_NAME}
              status={fieldErrors.shareName ? "error" : ""}
            />
            {fieldErrors.shareName && (
              <Typography.Text type="danger" style={{ fontSize: 12 }}>
                {fieldErrors.shareName}
              </Typography.Text>
            )}
          </div>
          <div style={{ marginBottom: 16 }}>
            <label>Prefix</label>
            <Input
              value={sharePrefix}
              onChange={e => {
                setSharePrefix(e.target.value);
                setFieldErrors(prev => ({ ...prev, sharePrefix: "" }));
              }}
              placeholder="e.g. folder/subfolder/"
              maxLength={MAX_PREFIX}
              status={fieldErrors.sharePrefix ? "error" : ""}
            />
            {fieldErrors.sharePrefix && (
              <Typography.Text type="danger" style={{ fontSize: 12 }}>
                {fieldErrors.sharePrefix}
              </Typography.Text>
            )}
          </div>
          <div style={{ marginBottom: 16 }}>
            <label>Description</label>
            <Input.TextArea
              value={shareDescription}
              onChange={e => {
                setShareDescription(e.target.value);
                setFieldErrors(prev => ({ ...prev, shareDescription: "" }));
              }}
              placeholder="Enter description"
              maxLength={MAX_DESCRIPTION}
              rows={2}
              status={fieldErrors.shareDescription ? "error" : ""}
            />
            {fieldErrors.shareDescription && (
              <Typography.Text type="danger" style={{ fontSize: 12 }}>
                {fieldErrors.shareDescription}
              </Typography.Text>
            )}
          </div>
        </Modal>
      </Card>
    </div>
  );
};

export default FileShares;
